import * as React from "react";
import { Link } from "react-router-dom";

type RLinkProps = {
  to?: string;
  href?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseOver?: () => void;
  onMouseOut?: () => void;
  /** @deprecated Use variant="white" instead */
  white?: boolean;
  /** @deprecated Use variant="amber" instead */
  amber?: boolean;
  download?: boolean;
  noUnderline?: boolean;

  /**
   * default: standard link styling, amber: dont use please, unstyled: no
   * styling (just cursor-pointer) - just for making other elements that need to
   * be clickable, like clickable cards
   * */
  variant?: "default" | "unstyled" | "secondary";

  // allow arbitrary data-* attrs for testing
  [key: `data-${string}`]: unknown;
};

const buildClassName = (
  variant: "default" | "secondary" | "unstyled",
  noUnderline: boolean | undefined,
  extra?: string
): string => {
  if (variant === "unstyled") {
    return ["cursor-pointer", extra].filter(Boolean).join(" ");
  }

  const variantClasses = {
    default:
      "text-slate-600 hover:text-slate-900 dark:text-white dark:hover:text-white",
    secondary:
      "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200 ",
  };

  const base = variantClasses[variant];
  const decoration = noUnderline ? "no-underline" : "hover:underline";

  return [base, decoration, "transition-colors", extra]
    .filter(Boolean)
    .join(" ");
};

const isExternal = (to: string): boolean => {
  return /^(https?:)?\/\//.test(to) || /^javascript:/.test(to);
};

export const RLink = ({
  to,
  href,
  children,
  className,
  style,
  onClick,
  onMouseOver,
  onMouseOut,
  noUnderline,
  variant,
  download,
  ...rest
}: RLinkProps): JSX.Element => {
  const destination = href ?? to ?? "";
  const combinedClassName = buildClassName(
    variant ?? "default",
    noUnderline,
    className
  );

  if (isExternal(destination)) {
    return (
      <a
        className={combinedClassName}
        style={style}
        download={download}
        href={destination}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        {...rest}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      className={combinedClassName}
      style={style}
      to={destination}
      {...rest}
      onClick={(e) => {
        onClick && onClick(e as unknown as React.MouseEvent<HTMLAnchorElement>);
      }}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
    >
      {children}
    </Link>
  );
};
